"use client";
import Link from "next/link";
import { useSearchParams, usePathname } from "next/navigation";
import { useRouter } from "next13-progressbar";
import { useState, useEffect, useRef } from "react";
import {
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
  CloseButton,
} from "@headlessui/react";
import { Bars3Icon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { CartIcon } from "../header/cartIcon";
import { SearchIcon } from "./searchIcon";
import { useCookies } from "react-cookie";
import { ServiceSummary } from "@/app/(shop)/services/[slug]/components/serviceSummary";
import { CartDrawer } from "../cartDrawer/cartDrawer";
import { SecondaryServicesNavigationBar } from "./secondaryServicesNavigationBar";
import { MobileNavigationBar } from "./mobileNavigationBar";
import { getCartItems } from "@/utils/cartUtils";
import { getServiceCartItems } from "@/app/(shop)/services/[slug]/components/serviceSummary";
import { SearchField } from "./searchField";
import { Perks } from "./perks";
import { NavLink } from "./navLink";
import { UtilityPanel } from "./utilityPanel";
import { BusinessHoursModal } from "../header/hoursModal";
import { getTimezoneAbbreviation } from "@/utils/timeZoneAbbreviation";
import { daysOfWeek } from "../merchantFooter/merchantFooter";
import { RestaurantCartDrawer } from "../restaurantCartDrawer/restaurantCartDrawer";
import { EditAddressesModal } from "@/components/editAddressesModal/editAddressesModal";
import ClockIcon from "@/components/_icons/clockIcon";
import { getOrderEstimate, saveDefaultAddress } from "@/dataLayer";
import { DeliveryModalError } from "../deliveryModalError/deliveryModalError";
import { Merchant } from "@/dto/merchant";
import { Category } from "@/dto/category";
import { giftCardCategory } from "@/utils/giftCardUtils";

export interface NavigationBarProps {
  merchant: Merchant;
  menu: any;
  customerPerks: any;
  merchantPreferences: any;
  deleteSession: any;
  retailCategories?: any;
  serviceCategories?: any;
  retailPickupTime: any;
}

export const NavigationBar = ({
  merchant,
  menu,
  customerPerks,
  merchantPreferences,
  deleteSession,
  retailCategories,
  serviceCategories,
  retailPickupTime,
}: NavigationBarProps) => {
  const pathName = usePathname();
  const { push } = useRouter();
  const [cookies] = useCookies(["session", "preview"]);
  const { session } = cookies;
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [searchFieldOpen, setSearchFieldOpen] = useState(false);
  const [perksModalIsOpen, setPerksModalIsOpen] = useState(false);
  const [loginModalIsOpen, setLoginModalIsOpen] = useState(false);
  const [storedCart, setStoredCart] = useState([]);
  const [isPreview, setIsPreview] = useState(false);
  const [tempAddress, setTempAddress] = useState("");
  const [hoursModalIsOpen, setHoursModalIsOpen] = useState(false);
  const [toCheckoutAfterLogin, setToCheckoutAfterLogin] = useState(false);
  const merchantMeta = merchant.merchantMeta;
  const merchantColor = merchantMeta?.colorPalette?.color;
  const searchParams = useSearchParams();
  const prevCartLength = useRef(storedCart.length);
  const hasMounted = useRef(false);
  const orderEstimateUpdateInterval = useRef(undefined);
  const showPerks =
    !merchant.isService ||
    (merchant.isService &&
      (merchantPreferences?.appointmentPref?.apptPayment === "FULL" ||
        merchant.enforcedCoFEnabled));
  const customerAuthEnabled: boolean =
    process.env.NEXT_PUBLIC_AUTH_ENABLED === "true";
  const hasOnlyRetailItems = merchant.itemTypesBitmap === 4;

  const enabledServices = merchant.services.filter((s: any) => s.enabled);

  const nonEssentialRestaurant =
    merchant.isRestaurant && merchant.isCheckoutEnabled;
  const nameToUseOnMenuLink = `${
    nonEssentialRestaurant ? "menu" : "categories"
  }`;
  const storedGiftCard =
    global?.window?.localStorage.getItem("giftCard") || null;

  const [hasNavigationMounted, setHasNavigationMounted] = useState(false);
  useEffect(() => {
    setHasNavigationMounted(true);
  }, []);

  useEffect(() => {
    if (window.location.href?.includes("#cart")) {
      setIsCartOpen(true);
    }
    if (window.location.href?.includes("#restaurant-cart")) {
      setIsRestaurantCartOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName]);

  useEffect(() => {
    setIsPreview(window.location.hostname.startsWith("preview-"));
  }, []);

  useEffect(() => {
    const updatePostInfo = setInterval(() => {
      const cart = () => {
        return getServiceCartItems()?.concat(getCartItems());
      };
      merchant.isService ? getServiceCartItems() : getCartItems();
      setStoredCart(cart() as any);
      const currentCartLength = storedCart.reduce(
        (adder: any, item: any) => adder + item.value?.quantity,
        0
      );

      if (hasMounted.current) {
        if (currentCartLength > prevCartLength.current) {
          handleCartDrawer();
        }
      } else {
        hasMounted.current = true;
      }

      prevCartLength.current = currentCartLength;
    }, 1000);
    return () => clearInterval(updatePostInfo);
  }, []);

  let uncategorisedItems = menu.find(
    (category: any) => category.id === "uncategorised"
  );
  let categories = Object.values(
    menu.filter((item: any) => item.id !== "uncategorised")
  ) as any[];
  const uncategorized = {
    id: "99999999999999",
    items: [],
    name: "Uncategorized",
    sortOrder: Number.MAX_SAFE_INTEGER,
  };
  if (uncategorisedItems && uncategorisedItems?.numberOfItems != 0)
    categories.push(uncategorized);

  let navigationCategories = merchantMeta?.navigationCategories;
  let mappedCategories = navigationCategories?.map((orderedCategory: any) => {
    const categoryDetails = categories.find(
      (category) => category.id === orderedCategory.value
    );
    return categoryDetails ? categoryDetails : null;
  });

  mappedCategories =
    mappedCategories?.filter((category: any) => category !== null) || [];

  const remainingCategories = categories.filter((category) => {
    return !mappedCategories.some(
      (mappedCategory: any) => mappedCategory.id === category.id
    );
  });

  if (merchantMeta?.selectedSetup === "Simple") {
    while (mappedCategories?.length < 5 && remainingCategories.length > 0) {
      mappedCategories.push(remainingCategories.shift());
    }
  }
  const moreCategories = remainingCategories.filter(
    (category: Category) => category.id !== giftCardCategory.id
  );

  const handlePerksModal = () => {
    setPerksModalIsOpen(!perksModalIsOpen);
  };

  const handleLoginModal = (toCheckout: boolean) => {
    setIsCartOpen(false);
    setToCheckoutAfterLogin(toCheckout);
    setLoginModalIsOpen(!loginModalIsOpen);
  };

  const handleHoursModal = () => {
    setHoursModalIsOpen(!hoursModalIsOpen);
  };
  const abbreviation = getTimezoneAbbreviation(
    merchant.timezone,
    merchant.locale
  );
  const serviceCart = JSON.parse(
    global?.window?.localStorage.getItem("serviceCart") || "[]"
  );
  const retailCart = JSON.parse(
    global?.window?.localStorage.getItem("cart") || "[]"
  );
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isAppointmentSummaryOpen, setIsAppointmentSummaryOpen] =
    useState(false);
  const [isRestaurantCartOpen, setIsRestaurantCartOpen] = useState(false);
  const [deliveryAddressModalOpen, setDeliveryAddressModalOpen] =
    useState(false);
  const [deliveryAddressErrorModalOpen, setDeliveryAddressErrorModalOpen] =
    useState(false);
  const [searchQuery, setSerchQuery] = useState("");

  const handleCartDrawer = () => {
    if (
      merchant.isService &&
      !merchant.isHybrid &&
      !merchant.hasOnlyRetailItems &&
      !storedGiftCard
    ) {
      setIsAppointmentSummaryOpen(!isAppointmentSummaryOpen);
    } else if (merchant.isRestaurant) {
      setIsRestaurantCartOpen(!isRestaurantCartOpen);
    } else if (merchant.isHybrid && retailCart.length >= 1) {
      setIsCartOpen(!isCartOpen);
    } else if (merchant.isHybrid && serviceCart.length >= 1)
      setIsAppointmentSummaryOpen(!isAppointmentSummaryOpen);
    else {
      setIsCartOpen(!isCartOpen);
    }
  };

  // update order estimate every 60 seconds if once of the carts is open
  useEffect(() => {
    const updateOrderEstimates = async () => {
      const orderEstimates = await getOrderEstimate(
        merchant.id,
        deliveryAddress,
        merchant?.isRetail ? retailPickupTime?.pickupDateTimeMillis : undefined
      );
      orderEstimates &&
        localStorage.setItem("orderEstimates", JSON.stringify(orderEstimates));
    };
    const deliveryAddress = JSON.parse(
      global?.window?.localStorage.getItem("deliveryAddress") || "{}"
    );
    if (
      (isCartOpen || isRestaurantCartOpen) &&
      deliveryAddress &&
      !orderEstimateUpdateInterval.current
    ) {
      updateOrderEstimates();
      (orderEstimateUpdateInterval.current as any) = setInterval(async () => {
        updateOrderEstimates();
      }, 60 * 1000);
    } else {
      clearInterval(orderEstimateUpdateInterval.current);
      orderEstimateUpdateInterval.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCartOpen, isRestaurantCartOpen]);

  if (!hasNavigationMounted) {
    return <div className="h-16"></div>;
  }

  const handleSearch = (event: { key: string }) => {
    if (event.key === "Enter") {
      const params = new URLSearchParams(searchParams || "");
      if (searchQuery) {
        params.set("query", searchQuery);
      } else {
        params.delete("query");
      }
      if (pathName.toLocaleLowerCase().includes("shop")) {
        push(`/shop?${params.toString()}`);
      } else if (merchant.isService) {
        push(`/services/all?${params.toString()}`);
      } else if (merchant.isRestaurant) {
        push(`/menu/all?${params.toString()}`);
      } else {
        push(`/all?${params.toString()}`);
      }
      setSearchFieldOpen(false);
    }
  };

  const showMoreDropDown = () => {
    // if we already display the only item in the more categories
    const currentCategoryInPathName = decodeURIComponent(pathName).includes(
      moreCategories[0]?.name
    );
    if (
      (moreCategories.length === 1 && currentCategoryInPathName) ||
      moreCategories.length < 1
    ) {
      return false;
    } else {
      return true;
    }
  };

  const moreDropDown = showMoreDropDown();

  return (
    <div className="flex w-full items-center justify-between sticky sm:relative top-0 pr-4">
      <div className="flex sm:hidden pl-6 py-6 sm:px-8 ">
        <button
          type="button"
          onClick={() => setSideMenuOpen(true)}
          className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
        >
          <span className="sr-only">Open main menu</span>
          <Bars3Icon aria-hidden="true" className="h-6 w-6" />
        </button>
      </div>
      {searchFieldOpen ? (
        <SearchField
          {...{
            searchParams,
            setSerchQuery,
            handleSearch,
            setSearchFieldOpen,
          }}
        />
      ) : (
        <div className="flex flex-col w-full items-end sm:items-center justify-between ">
          <nav
            aria-label="Global"
            className={[
              "flex",
              "items-center",
              "justify-between",
              "sm:w-full",
              "sm:px-8",
            ].join(" ")}
          >
            <PopoverGroup className="hidden sm:flex pt-5">
              {merchantMeta?.selectedSetup !== "Simple" && (
                <NavLink
                  name={"about"}
                  style={{ borderColor: merchantColor }}
                  showBottomBorder={false}
                />
              )}
              {merchant.isService && !hasOnlyRetailItems && (
                <NavLink
                  name={"services"}
                  style={{ borderColor: merchantColor }}
                  showBottomBorder={pathName === "/services/all"}
                />
              )}
              {(merchant.isHybrid ||
                (merchant.isService && hasOnlyRetailItems)) && (
                <NavLink
                  name={"shop"}
                  style={{ borderColor: merchantColor }}
                  showBottomBorder={false}
                />
              )}
              {(merchant.isRestaurant || merchant.isEssentialMerchant) && (
                <NavLink
                  name={`${nameToUseOnMenuLink}`}
                  nameToUse="menu"
                  style={{ borderColor: merchantColor }}
                  showBottomBorder={
                    "menu catalog".includes(nameToUseOnMenuLink) &&
                    pathName.includes("/menu")
                  }
                />
              )}
              {merchant.isRetail &&
                mappedCategories?.map((c: { id: string; name: string }) => (
                  <NavLink
                    key={c.id}
                    name={encodeURIComponent(c.name)}
                    style={{ borderColor: merchantColor }}
                    showBottomBorder={false}
                  />
                ))}
              {moreCategories.some(
                (c: { id: string; name: string }) =>
                  decodeURIComponent(pathName || "").replace("/", "") === c.name
              ) && (
                <NavLink
                  name={(pathName || "").substring(1)}
                  style={{ borderColor: merchantColor }}
                  showBottomBorder={
                    ((pathName || "").substring(1) === "services" &&
                      pathName === "/services/all") ||
                    ("menu catalog".includes((pathName || "").substring(1)) &&
                      pathName.includes("/menu"))
                  }
                />
              )}

              {merchant.isRetail && moreDropDown && (
                <Popover className="relative">
                  <PopoverButton
                    className={[
                      "flex",
                      "items-center",
                      "gap-x-2",
                      "leading-6",
                      "px-4",
                      "border-b-4",
                      "border-transparent",
                      "text-gray-900",
                      "hover:bg-gray-50",
                      "font-normal text-base leading-6",
                    ].join(" ")}
                  >
                    More
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="h-5 w-5 flex-none text-gray-900"
                    />
                  </PopoverButton>
                  <PopoverPanel
                    transition
                    className="absolute -left-8 top-full z-20 mt-3 w-screen max-w-[400px] overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in max-h-[500px] overflow-y-auto p-3"
                  >
                    {moreCategories
                      .filter(
                        (c: { id: string; name: string }) =>
                          decodeURIComponent(pathName || "").replace(
                            "/",
                            ""
                          ) !== c.name
                      )
                      .map((c: { id: string; name: string }) => (
                        <div
                          key={c.id}
                          className="group relative flex items-center gap-x-6 rounded-lg p-3 text-sm leading-6 hover:bg-gray-50"
                        >
                          <div className="flex-auto">
                            <Link
                              href={`/${c.name}`}
                              as={`/${encodeURIComponent(c.name)}`}
                              className={[
                                "block text-gray-900",
                                "font-normal text-base leading-6",
                              ].join(" ")}
                            >
                              <CloseButton>
                                {" "}
                                {c.name}
                                <span className="absolute inset-0" />
                              </CloseButton>
                            </Link>
                          </div>
                        </div>
                      ))}
                  </PopoverPanel>
                </Popover>
              )}
              {merchant.isSellingGiftCards && (
                <NavLink
                  name={"gift card"}
                  nameToUse={"giftcard"}
                  style={{ borderColor: merchantColor }}
                  showBottomBorder={pathName.includes("/giftcard")}
                  data-testid="gift-card-navlink"
                />
              )}
            </PopoverGroup>
            <div className={["flex flex-row gap-6 items-center "].join(" ")}>
              {session &&
                customerPerks?.length > 0 &&
                showPerks &&
                merchant.isCheckoutEnabled && (
                  <Perks
                    {...{
                      handlePerksModal,
                      perksModalIsOpen,
                      customerPerks,
                    }}
                  />
                )}
              {!isPreview && !pathName?.includes("/giftcard") && (
                <div
                  className={["cursor-pointer"].join(" ")}
                  onClick={() => setSearchFieldOpen(true)}
                >
                  <SearchIcon />
                </div>
              )}
              {merchantMeta?.selectedSetup !== "Custom" && (
                <div onClick={handleHoursModal} className="sm:hidden">
                  <ClockIcon />
                </div>
              )}
              <BusinessHoursModal
                isOpen={hoursModalIsOpen}
                onRequestClose={handleHoursModal}
                daysOfWeek={daysOfWeek}
                businessHours={merchant?.businessHours?.hours}
                timezone={abbreviation}
              />
              {!isPreview &&
                merchant.isCheckoutEnabled &&
                customerAuthEnabled && (
                  <UtilityPanel
                    {...{
                      session,
                      handleLoginModal,
                      loginModalIsOpen,
                      setLoginModalIsOpen,
                      deleteSession,
                      merchantColor,
                      toCheckoutAfterLogin,
                    }}
                    merchant={merchant}
                  />
                )}
              {merchant.isCheckoutEnabled && !isPreview && (
                <div
                  className={["cursor-pointer", "relative", "inline-flex"].join(
                    " "
                  )}
                  onClick={handleCartDrawer}
                  data-testid="cart-icon"
                >
                  <CartIcon />
                  {(storedCart.length > 0 || storedGiftCard) && (
                    <span
                      data-testid="cart-number"
                      className="absolute flex left-2 top-2"
                    >
                      <div
                        style={{ backgroundColor: merchantColor }}
                        className="flex items-center justify-center text-white rounded-full w-5 h-5 text-xs"
                      >
                        {`${
                          storedCart.reduce(
                            (adder: any, item: any) =>
                              adder + item.value?.quantity,
                            0
                          ) + (storedGiftCard ? 1 : 0)
                        }`}
                      </div>
                    </span>
                  )}
                </div>
              )}
              <CartDrawer
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
                onRequestClose={handleCartDrawer}
                merchant={merchant}
                onTriggerLoginModal={handleLoginModal}
                retailPickupTime={retailPickupTime}
                setDeliveryAddressModalOpen={setDeliveryAddressModalOpen}
                setIsCartOpen={setIsCartOpen}
              />
              <ServiceSummary
                isOpen={isAppointmentSummaryOpen}
                onClose={() => setIsAppointmentSummaryOpen(false)}
                onRequestClose={() => setIsAppointmentSummaryOpen(false)}
                merchant={merchant}
                onTriggerLoginModal={handleLoginModal}
                merchantPreferences={merchantPreferences}
                merchantColor={merchantColor}
                setIsCartOpen={setIsAppointmentSummaryOpen}
              />
              <RestaurantCartDrawer
                isOpen={isRestaurantCartOpen}
                onClose={() => setIsRestaurantCartOpen(false)}
                onRequestClose={handleCartDrawer}
                merchant={merchant}
                onTriggerLoginModal={handleLoginModal}
                setDeliveryAddressModalOpen={setDeliveryAddressModalOpen}
                setIsRestaurantCartOpen={setIsRestaurantCartOpen}
              />
              <EditAddressesModal
                setDefaultDeliveryAllowed={!!session}
                merchant={merchant}
                merchantColor={merchantColor}
                isOpen={deliveryAddressModalOpen}
                onRequestClose={() => setDeliveryAddressModalOpen(false)}
                onUpdated={async (address: any, isDefaultDelivery: boolean) => {
                  setTempAddress(address);
                  const result = await getOrderEstimate(merchant.id, address);
                  if (result?.status?.code === 400) {
                    setDeliveryAddressModalOpen(false);
                    setDeliveryAddressErrorModalOpen(true);
                  } else {
                    if (isDefaultDelivery) {
                      await saveDefaultAddress(address);
                    }
                    localStorage.setItem(
                      "orderEstimates",
                      JSON.stringify(result)
                    );
                    localStorage.setItem(
                      "deliveryAddress",
                      JSON.stringify(address)
                    );
                  }
                }}
                checkoutCloseHandler={() => {}}
              />
              <DeliveryModalError
                onRequestClose={() => setDeliveryAddressErrorModalOpen(false)}
                merchantColor={merchantColor}
                isOpen={deliveryAddressErrorModalOpen}
                merchant={merchant}
                address={tempAddress}
                onEnterNewAddress={() => {
                  setDeliveryAddressErrorModalOpen(false);
                  setDeliveryAddressModalOpen(true);
                }}
                checkoutCloseHandler={() => {}}
              />
            </div>
          </nav>
          <div className="hidden sm:flex w-[98%] border-t-2 ml-4"></div>
          {(pathName?.includes("/services") ||
            pathName?.includes("/menu") ||
            (pathName?.includes("/shop") && merchant.isHybrid)) && (
            <SecondaryServicesNavigationBar
              displayCategories={mappedCategories}
              moreCategories={moreCategories}
              retailCategories={retailCategories}
              serviceCategories={serviceCategories}
            />
          )}
        </div>
      )}
      <MobileNavigationBar
        categories={categories}
        sideMenuOpen={sideMenuOpen}
        setSideMenuOpen={setSideMenuOpen}
        isServices={merchant.isService}
        isRestaurantMerchant={merchant.isRestaurant}
        merchantMeta={merchantMeta}
        isHybridMerchant={merchant.isHybrid}
        isSellingGiftCards={merchant.isSellingGiftCards}
      />
    </div>
  );
};
